import { useEffect, useRef } from "react";

const POLL_INTERVAL_MS = 2500;
const POLL_WINDOW_MS = 35000;
const STORAGE_PREFIX = "loyalty:postPurchase:";

/** À appeler après un checkout / vente test pour activer le polling sur la fiche client. */
export function markPostPurchasePolling(profileId) {
    if (!profileId) return;
    sessionStorage.setItem(`${STORAGE_PREFIX}${profileId}`, String(Date.now() + POLL_WINDOW_MS));
}

export function shouldPollPostPurchase(profileId) {
    if (!profileId) return false;
    const until = Number(sessionStorage.getItem(`${STORAGE_PREFIX}${profileId}`) || 0);
    return until > Date.now();
}

export function clearPostPurchasePolling(profileId) {
    if (!profileId) return;
    sessionStorage.removeItem(`${STORAGE_PREFIX}${profileId}`);
}

/**
 * Poll loyalty + transactions every ~2.5 s for up to 35 s after achat.
 * @param {() => void | Promise<void>} onPoll — refetch loyalty + ventes
 */
export function usePostPurchasePolling({ brandName, profileId, onPoll, enabled = true }) {
    const onPollRef = useRef(onPoll);
    onPollRef.current = onPoll;

    useEffect(() => {
        if (!enabled || !brandName || !profileId) return undefined;
        if (!shouldPollPostPurchase(profileId)) return undefined;

        const startedAt = Date.now();
        let cancelled = false;

        const tick = async () => {
            try {
                await onPollRef.current?.();
            } catch {
                /* ignore transient poll errors */
            }
        };

        tick();

        const intervalId = setInterval(() => {
            if (cancelled) return;
            if (Date.now() - startedAt >= POLL_WINDOW_MS) {
                clearInterval(intervalId);
                clearPostPurchasePolling(profileId);
                return;
            }
            tick();
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [brandName, profileId, enabled]);
}
